import { useQuery } from "@apollo/client";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import {
  GridColDef,
  gridVisibleColumnDefinitionsSelector,
  useGridApiContext,
  useGridEvent,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import type { Row } from "./TasksDisplay";

import { AnimatedHeight } from "./AnimatedHeight";
import { TASKS_QUERY } from "./schema";
import {
  CELL_PADDING,
  getSeriesChildGridSx,
  getSeriesPanelSx,
  getSeriesShowMoreSx,
  SERIES_TOGGLE_WIDTH,
  seriesSkeletonRowSx,
} from "./seriesGroupStyles";

const PAGE_STEP = 3;

/** Leading column standing in for the parent's toggle column. */
const SPACER_FIELD = "__detailSpacer__";

type TaskSeriesDetailPanelProps = {
  seriesId: string;
  tenantId: string | undefined;
  /** The next-instance deadline shown on the series row; occurrences after this are listed. */
  afterDeadline: Date;
  /**
   * Render definitions for the data columns, keyed by field. Column widths,
   * order, and visibility are taken from the live parent grid instead — these
   * supply only how each cell renders, so occurrences look like parent rows.
   */
  columns: GridColDef<Row>[];
  onRowClick: (row: Row) => void;
};

/**
 * Lazily loads the occurrences of a series that follow its next instance, in
 * steps of {@link PAGE_STEP}. Reuses the `virtualEvents` query filtered by
 * `seriesId`, and mirrors the parent grid's visible columns (widths, order, and
 * manage-columns visibility) so each occurrence row lines up with the parent.
 */
export const TaskSeriesDetailPanel = ({
  seriesId,
  tenantId,
  afterDeadline,
  columns,
  onRowClick,
}: TaskSeriesDetailPanelProps) => {
  const theme = useTheme();
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);

  // The detail panel renders inside the parent grid's context, so this is the
  // parent's api. Its visible column definitions carry the resolved widths
  // (flex, user resizes) and manage-columns visibility, so reading them keeps
  // the panel aligned with the parent and reacts to resizing/toggling live.
  const parentApiRef = useGridApiContext();
  const visibleParentColumns = useGridSelector(parentApiRef, gridVisibleColumnDefinitionsSelector);

  // The committed width (computedWidth) only updates when a resize ends, so the
  // panel would jump to catch up. Track the in-flight width during a drag and
  // clear it once the resize commits, so the child column tracks live.
  const [liveResize, setLiveResize] = useState<{ field: string; width: number } | null>(null);
  useGridEvent(parentApiRef, "columnResize", (params) =>
    setLiveResize({ field: params.colDef.field, width: params.width })
  );
  useGridEvent(parentApiRef, "columnResizeStop", () => setLiveResize(null));

  const renderByField = useMemo(() => {
    const map = new Map<string, GridColDef<Row>>();
    for (const column of columns) map.set(column.field, column);
    return map;
  }, [columns]);

  // A leading spacer matching the parent's toggle column (which isn't a data
  // column), then each visible data column mirrored at its current computed
  // width. Filtering to known data fields keeps order, resizes, and
  // manage-columns visibility in sync while reserving the toggle's space.
  const panelColumns = useMemo<GridColDef<Row>[]>(() => {
    const spacer: GridColDef<Row> = {
      field: SPACER_FIELD,
      headerName: "",
      sortable: false,
      width: SERIES_TOGGLE_WIDTH,
      minWidth: 0,
      renderCell: () => null,
    };
    const dataColumns = visibleParentColumns
      .filter((parentColumn) => renderByField.has(parentColumn.field))
      .map((parentColumn) => {
        const base = renderByField.get(parentColumn.field);
        const width =
          liveResize?.field === parentColumn.field ? liveResize.width : parentColumn.computedWidth;
        return { ...base, field: parentColumn.field, width, minWidth: 0, flex: undefined };
      });
    return [spacer, ...dataColumns];
  }, [visibleParentColumns, renderByField, liveResize]);

  // Occurrences strictly after the next instance. The server compares against
  // UTC midnight, so step from the UTC date; a local date can land on or before
  // the next instance and repeat it as the first child.
  const after = DateTime.fromJSDate(new Date(afterDeadline), { zone: "utc" })
    .plus({ days: 1 })
    .toISODate();
  const search = [tenantId ? `tenantId:${tenantId}` : "", `seriesId:${seriesId}`, `after:${after}`]
    .filter(Boolean)
    .join(" ");

  const { data, previousData, loading } = useQuery(TASKS_QUERY, {
    variables: {
      search,
      page: 0,
      pageSize: visibleCount,
      sort: [{ field: "deadline", sort: "asc" }],
    },
    fetchPolicy: "cache-and-network",
  });

  // While a larger page loads, keep showing the previous rows (same stable ids)
  // so expanding appends rows instead of flashing the whole panel.
  const current = data ?? previousData;
  const items = current?.virtualEvents.items ?? [];
  const totalCount = current?.virtualEvents.count ?? 0;
  const hasMore = totalCount > items.length;
  // Before any rows arrive, or while the next page loads, a single skeleton row
  // stands in: the series may have fewer occurrences than a full page.
  const isInitialLoad = loading && !current;
  const isLoadingMore = loading && hasMore && items.length < visibleCount;

  return (
    <AnimatedHeight>
      <Stack sx={getSeriesPanelSx(theme)}>
        {isInitialLoad ? (
          <SkeletonRow columns={panelColumns} />
        ) : (
          <>
            <ChildGrid items={items} columns={panelColumns} onRowClick={onRowClick} />
            {isLoadingMore && <SkeletonRow columns={panelColumns} />}
            {hasMore && !isLoadingMore && (
              <ShowMore onClick={() => setVisibleCount((count) => count + PAGE_STEP)} />
            )}
          </>
        )}
      </Stack>
    </AnimatedHeight>
  );
};

const ChildGrid = ({
  items,
  columns,
  onRowClick,
}: {
  items: Row[];
  columns: GridColDef<Row>[];
  onRowClick: (row: Row) => void;
}) => {
  const theme = useTheme();
  return (
    <DataGrid<Row>
      rows={items}
      columns={columns}
      autoHeight
      getRowHeight={() => "auto"}
      hideFooter
      columnHeaderHeight={0}
      disableRowSelectionOnClick
      disableColumnMenu
      onRowClick={({ row }) => onRowClick(row)}
      noRowsMessage="No further occurrences in this series."
      sx={getSeriesChildGridSx(theme)}
    />
  );
};

/** A placeholder child row whose cells line up with the panel's columns. */
const SkeletonRow = ({ columns }: { columns: GridColDef<Row>[] }) => (
  <Box sx={seriesSkeletonRowSx} aria-busy="true" aria-label="Loading occurrences">
    {columns.map((column) => (
      <Box
        key={column.field}
        sx={{
          width: column.width,
          flexShrink: 0,
          px: `${CELL_PADDING}px`,
        }}
      >
        {column.field !== SPACER_FIELD && column.type !== "actions" && (
          <Skeleton variant="text" sx={{ maxWidth: 120 }} />
        )}
      </Box>
    ))}
  </Box>
);

const ShowMore = ({ onClick }: { onClick: () => void }) => {
  const theme = useTheme();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      sx={getSeriesShowMoreSx(theme)}
    >
      <Typography variant="body2" color="primary">
        Show {PAGE_STEP} more
      </Typography>
    </Box>
  );
};
